/**
 * Edit Webhook Modal (September 2025)
 *
 * Modal component for editing existing webhook endpoints with TanStack Form validation.
 *
 * Features:
 * - Pre-populated form data from existing webhook
 * - Field-level validation with TanStack Form
 * - Event selection with checkboxes and search filter
 * - Terminal-themed styling with Tailwind CSS
 * - SSRF validation and HTTPS enforcement
 */

import { useState, useMemo, useEffect } from 'react';
import { useForm } from '@tanstack/react-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useConvex } from 'convex/react';
import { api } from '../../convex/_generated/api';
import type { Id } from '../../convex/_generated/dataModel';
import type {
  WebhookEventType,
  UpdateWebhookInput,
} from '@keypalace/shared-types';

interface Webhook {
  id: Id<'webhooks'>;
  name: string;
  url: string;
  status: 'enabled' | 'disabled';
  events: WebhookEventType[];
  retryConfig: {
    maxRetries: number;
    backoffMultiplier: number;
    initialDelayMs: number;
  };
  customHeaders?: Record<string, string>;
  description?: string;
  lastDeliveryAt?: number;
  lastDeliveryStatus?: 'success' | 'failure';
  lastErrorMessage?: string;
  successCount: number;
  failureCount: number;
  createdAt: number;
  updatedAt: number;
}

interface EditWebhookModalProps {
  isOpen: boolean;
  webhook: Webhook | null;
  onClose: () => void;
  onSuccess?: () => void;
}

const WEBHOOK_EVENTS: WebhookEventType[] = [
  'service_key.rotated',
  'service_key.created',
  'child_key.created',
  'child_key.revoked',
  'credits.low_threshold',
  'credits.exhausted',
];

const EVENT_LABELS: Record<WebhookEventType, string> = {
  'service_key.rotated': 'Service Key Rotated',
  'service_key.created': 'Service Key Created',
  'child_key.created': 'Child Key Created',
  'child_key.revoked': 'Child Key Revoked',
  'credits.low_threshold': 'Credits Low Threshold',
  'credits.exhausted': 'Credits Exhausted',
};

const EVENT_DESCRIPTIONS: Record<WebhookEventType, string> = {
  'service_key.rotated': 'Triggered when your service API key is rotated for security',
  'service_key.created': 'Triggered when a new service API key is generated',
  'child_key.created': 'Triggered when a child API key is created for an end user',
  'child_key.revoked': 'Triggered when a child API key is revoked',
  'credits.low_threshold': 'Triggered when credits drop below a configured threshold',
  'credits.exhausted': 'Triggered when credits reach zero',
};

export function EditWebhookModal({ isOpen, webhook, onClose, onSuccess }: EditWebhookModalProps) {
  const convex = useConvex();
  const queryClient = useQueryClient();
  const [eventSearch, setEventSearch] = useState('');

  const updateWebhookMutation = useMutation({
    mutationFn: async ({ webhookId, input }: { webhookId: Id<'webhooks'>; input: UpdateWebhookInput }) => {
      return await convex.mutation(api.webhooks.mutations.updateWebhook, {
        webhookId,
        updates: input,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhooks'] });
      if (onSuccess) {
        onSuccess();
      }
      handleClose();
    },
  });

  const form = useForm({
    defaultValues: {
      name: webhook?.name || '',
      url: webhook?.url || '',
      events: webhook?.events || [],
      status: webhook?.status || 'enabled',
      description: webhook?.description || '',
    },
    onSubmit: async ({ value }) => {
      if (!webhook) return;
      try {
        await updateWebhookMutation.mutateAsync({
          webhookId: webhook.id,
          input: value,
        });
      } catch (error) {
        console.error('Failed to update webhook:', error);
      }
    },
  });

  useEffect(() => {
    if (webhook) {
      form.setFieldValue('name', webhook.name);
      form.setFieldValue('url', webhook.url);
      form.setFieldValue('events', webhook.events);
      form.setFieldValue('status', webhook.status);
      form.setFieldValue('description', webhook.description || '');
    }
  }, [webhook, form]);

  const filteredEvents = useMemo(() => {
    if (!eventSearch.trim()) return WEBHOOK_EVENTS;

    const search = eventSearch.toLowerCase();
    return WEBHOOK_EVENTS.filter((event) => {
      const label = EVENT_LABELS[event].toLowerCase();
      const description = EVENT_DESCRIPTIONS[event].toLowerCase();
      return label.includes(search) || description.includes(search);
    });
  }, [eventSearch]);

  const handleClose = () => {
    form.reset();
    setEventSearch('');
    onClose();
  };

  if (!isOpen || !webhook) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-terminal-black/90 backdrop-blur-sm">
      <div className="terminal-box max-w-4xl w-full mx-4 p-8 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-terminal-orange terminal-glow">
            [EDIT WEBHOOK]
          </h2>
          <button
            onClick={handleClose}
            className="text-terminal-cyan hover:text-terminal-orange text-2xl font-bold"
          >
            ×
          </button>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}
          className="space-y-6"
        >
          <form.Field
            name="name"
            validators={{
              onChange: ({ value }) => {
                if (!value || value.length < 3) {
                  return 'Webhook name must be at least 3 characters';
                }
                if (value.length > 100) {
                  return 'Webhook name must not exceed 100 characters';
                }
                return undefined;
              },
            }}
          >
            {(field) => (
              <div>
                <label className="block text-terminal-cyan text-sm font-bold mb-2">
                  WEBHOOK NAME *
                </label>
                <input
                  type="text"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  placeholder="Production API Webhook"
                  className="w-full bg-terminal-black border-2 border-terminal-cyan text-terminal-orange p-3 focus:outline-none focus:border-terminal-orange placeholder-terminal-cyan/30"
                />
                {field.state.meta.errors.length > 0 && (
                  <p className="text-red-400 text-xs mt-1">
                    {field.state.meta.errors[0]}
                  </p>
                )}
              </div>
            )}
          </form.Field>

          <form.Field
            name="url"
            validators={{
              onChange: ({ value }) => {
                if (!value) {
                  return 'Webhook URL is required';
                }
                try {
                  const url = new URL(value);
                  if (url.protocol !== 'https:') {
                    return 'Webhook URL must use HTTPS protocol';
                  }
                  const hostname = url.hostname.toLowerCase();
                  if (
                    hostname === 'localhost' ||
                    hostname.startsWith('127.') ||
                    hostname.startsWith('10.') ||
                    hostname.startsWith('172.16.') ||
                    hostname.startsWith('192.168.') ||
                    hostname.startsWith('169.254.')
                  ) {
                    return 'Webhook URL blocked: potential SSRF attack detected';
                  }
                } catch {
                  return 'Invalid webhook URL format';
                }
                return undefined;
              },
            }}
          >
            {(field) => (
              <div>
                <label className="block text-terminal-cyan text-sm font-bold mb-2">
                  WEBHOOK URL *
                </label>
                <input
                  type="url"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  placeholder="https://api.example.com/webhooks/keypalace"
                  className="w-full bg-terminal-black border-2 border-terminal-cyan text-terminal-orange p-3 font-mono focus:outline-none focus:border-terminal-orange placeholder-terminal-cyan/30"
                />
                {field.state.meta.errors.length > 0 && (
                  <p className="text-red-400 text-xs mt-1">
                    {field.state.meta.errors[0]}
                  </p>
                )}
              </div>
            )}
          </form.Field>

          <form.Field
            name="status"
          >
            {(field) => (
              <div>
                <label className="block text-terminal-cyan text-sm font-bold mb-2">
                  STATUS
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={(field.state.value || 'enabled') === 'enabled'}
                      onChange={() => field.handleChange('enabled')}
                      className="w-4 h-4 accent-terminal-orange"
                    />
                    <span className="text-green-400 font-semibold">ENABLED</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={(field.state.value || 'enabled') === 'disabled'}
                      onChange={() => field.handleChange('disabled')}
                      className="w-4 h-4 accent-terminal-orange"
                    />
                    <span className="text-red-400 font-semibold">DISABLED</span>
                  </label>
                </div>
              </div>
            )}
          </form.Field>

          <form.Field
            name="events"
            validators={{
              onChange: ({ value }) => {
                if (!value || value.length === 0) {
                  return 'At least one event type must be selected';
                }
                return undefined;
              },
            }}
          >
            {(field) => (
              <div>
                <label className="block text-terminal-cyan text-sm font-bold mb-2">
                  EVENT TYPES * ({field.state.value.length} selected)
                </label>

                <input
                  type="text"
                  value={eventSearch}
                  onChange={(e) => setEventSearch(e.target.value)}
                  placeholder="Search events..."
                  className="w-full bg-terminal-black border-2 border-terminal-cyan text-terminal-orange p-3 mb-4 focus:outline-none focus:border-terminal-orange placeholder-terminal-cyan/30"
                />

                <div className="bg-terminal-black border-2 border-terminal-cyan p-4 max-h-64 overflow-y-auto space-y-3">
                  {filteredEvents.length === 0 ? (
                    <p className="text-terminal-cyan/50 text-sm">No events match your search</p>
                  ) : (
                    filteredEvents.map((event) => (
                      <label
                        key={event}
                        className="flex items-start gap-3 cursor-pointer hover:bg-terminal-cyan/5 p-2 transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={(field.state.value || []).includes(event)}
                          onChange={(e) => {
                            const currentEvents = field.state.value || [];
                            if (e.target.checked) {
                              field.handleChange([...currentEvents, event]);
                            } else {
                              field.handleChange(
                                currentEvents.filter((ev) => ev !== event)
                              );
                            }
                          }}
                          className="mt-1 w-4 h-4 accent-terminal-orange"
                        />
                        <div className="flex-1">
                          <div className="text-terminal-orange font-semibold text-sm">
                            {EVENT_LABELS[event]}
                          </div>
                          <div className="text-terminal-cyan/70 text-xs">
                            {EVENT_DESCRIPTIONS[event]}
                          </div>
                        </div>
                      </label>
                    ))
                  )}
                </div>

                {field.state.meta.errors.length > 0 && (
                  <p className="text-red-400 text-xs mt-1">
                    {field.state.meta.errors[0]}
                  </p>
                )}
              </div>
            )}
          </form.Field>

          <form.Field name="description">
            {(field) => (
              <div>
                <label className="block text-terminal-cyan text-sm font-bold mb-2">
                  DESCRIPTION (Optional)
                </label>
                <textarea
                  value={field.state.value || ''}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  placeholder="Additional details about this webhook..."
                  rows={3}
                  className="w-full bg-terminal-black border-2 border-terminal-cyan text-terminal-orange p-3 focus:outline-none focus:border-terminal-orange placeholder-terminal-cyan/30 resize-none"
                />
              </div>
            )}
          </form.Field>

          <div className="bg-terminal-black border-2 border-terminal-cyan p-4">
            <h3 className="text-terminal-orange font-bold mb-2">[WEBHOOK INFO]</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-terminal-cyan/70">Created:</span>
                <span className="text-terminal-cyan ml-2">
                  {new Date(webhook.createdAt).toLocaleDateString()}
                </span>
              </div>
              <div>
                <span className="text-terminal-cyan/70">Updated:</span>
                <span className="text-terminal-cyan ml-2">
                  {new Date(webhook.updatedAt).toLocaleDateString()}
                </span>
              </div>
              <div>
                <span className="text-terminal-cyan/70">Deliveries:</span>
                <span className="text-terminal-cyan ml-2">
                  {webhook.successCount + webhook.failureCount}
                </span>
              </div>
              <div>
                <span className="text-terminal-cyan/70">Success Rate:</span>
                <span className="text-green-400 ml-2">
                  {webhook.successCount + webhook.failureCount > 0
                    ? Math.round((webhook.successCount / (webhook.successCount + webhook.failureCount)) * 100)
                    : 0}%
                </span>
              </div>
            </div>
          </div>

          <div className="flex gap-4 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 bg-transparent text-terminal-cyan font-bold py-3 px-6 border-2 border-terminal-cyan hover:bg-terminal-cyan hover:text-terminal-black transition-all duration-300"
            >
              CANCEL
            </button>
            <button
              type="submit"
              disabled={updateWebhookMutation.isPending}
              className="flex-1 bg-terminal-orange text-terminal-black font-bold py-3 px-6 border-2 border-terminal-orange hover:bg-transparent hover:text-terminal-orange transition-all duration-300 terminal-glow disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {updateWebhookMutation.isPending ? 'SAVING...' : 'SAVE CHANGES'}
            </button>
          </div>

          {updateWebhookMutation.isError && (
            <div className="bg-red-900/20 border-2 border-red-400 p-4">
              <p className="text-red-400 text-sm font-bold">
                [ERROR] Failed to update webhook
              </p>
              <p className="text-red-400/70 text-xs mt-1">
                {(updateWebhookMutation.error as Error)?.message || 'Unknown error occurred'}
              </p>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
